import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { VpInfoMenu } from '@vp-ui/components';
import { copyToClipboard } from '@src/utils/copyToClipboard';
import { useVpSnackbar } from '@vp-ui/hooks';
import { useForceDownload } from '@src/hooks';
import { showSocialShareModal } from '@src/redux/modules/share';
import { useMediaTableContext } from '@src/hooks/contexts';
import { getDownloadOriginalVideoEnabled } from '@src/redux/modules/auth';
import { MEDIA_TABS } from '@src/constants/media-tabs';

const getMenuItems = (allowDownload, isVideo) => [
  {
    id: 'copy',
    label: 'Copy Link',
    icon: 'Copy'
  },
  {
    id: 'share',
    label: 'Share',
    icon: 'Share2'
  },
  {
    id: 'download',
    label: 'Download Media',
    icon: 'DownloadCloud'
  },
  {
    id: 'download_raw',
    label: 'Download Unprocessed Media',
    icon: 'Download',
    conditionalProps: {
      condition: !allowDownload
    }
  },
  ...(isVideo
    ? [
        {
          id: 'generate_captions',
          label: 'Generate Captions',
          icon: 'Type'
        }
      ]
    : []),
  {
    id: 'delete',
    label: 'Delete',
    icon: 'Trash2',
    variant: 'error'
  }
];

export const VpMediaInfoMenu = ({ media, _showSocialShareModal, downloadOriginalEnabled, history }) => {
  const id = media?.mediaId || media?.attachmentId;
  const isVideo = !!media?.mediaId;

  const MENU_ITEMS = getMenuItems(downloadOriginalEnabled, isVideo);

  const { addSnackbarInfo } = useVpSnackbar();
  const { forceDownload, isLoading } = useForceDownload();
  const {
    setSelectedEntity,
    setSelectedSubmissionId,
    setSelectedMediaId,
    setPendingTab,
    getSubmissionIdByMediaId,
    selectedSubmissionId,
  } = useMediaTableContext();
  const onSelect = (item) => {
    switch (item?.id) {
      case 'copy': {
        copyToClipboard(`${window?.location?.origin}/share/${media?.mediaRawPath}`);
        addSnackbarInfo({ message: 'Copied link to clipboard', title: '' });
        return;
      }
      case 'share': {
        // this is the description of the question, not the description of the video that the user can update
        //   so we want to blank this out so it doesn't show in the youtube description box
        _showSocialShareModal(null, { ...media, description: '' });
        setSelectedSubmissionId(null);
        return;
      }
      case 'download': {
        const mediaUrlSplit = media?.mediaUrl?.split('/');
        const fileName = mediaUrlSplit[mediaUrlSplit.length - 1];
        forceDownload(media?.mediaUrl, fileName);
        return;
      }
      case 'download_raw': {
        const mediaUrlSplit = media?.originalPath?.split('/');
        const fileName = mediaUrlSplit[mediaUrlSplit.length - 1];
        forceDownload(media?.originalPath, fileName);
        return;
      }
      case 'generate_captions': {
        const subId = getSubmissionIdByMediaId(media?.mediaId);
        if (subId) {
          setSelectedMediaId(media?.mediaId);
          if (selectedSubmissionId !== subId) {
            // Navigate to a different submission: pass pendingTab via location state
            // so it survives the route remount
            setSelectedSubmissionId(subId);
            history.push(`/media/${subId}`, { pendingTab: MEDIA_TABS.CAPTION });
          } else {
            // Dialog already open for this submission — context stays alive, use state directly
            setPendingTab(MEDIA_TABS.CAPTION);
          }
        }
        return;
      }
      case 'delete': {
        setSelectedEntity(media);
        return;
      }
    }
  };

  return <VpInfoMenu id={id} loading={isLoading} menuItems={MENU_ITEMS} onSelect={onSelect} />;
};

VpMediaInfoMenu.propTypes = {
  _showSocialShareModal: PropTypes.func,
  media: PropTypes.object,
  downloadOriginalEnabled: PropTypes.bool,
  history: PropTypes.object
};

const mapStateToProps = (state) => ({
  downloadOriginalEnabled: getDownloadOriginalVideoEnabled(state)
});
const mapDispatchToProps = {
  _showSocialShareModal: showSocialShareModal
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VpMediaInfoMenu));



// WEBPACK FOOTER //
// ./src/vp-ui/widgets/VpMediaTable/VpMediaInfoMenu.jsx